export type PeriodTier = "primary" | "overflow" | "custom";
export type PeriodGroup = "trailing" | "inception";

export interface Period {
  id: string;
  label: string;
  months: number;
  tier?: PeriodTier;
  group?: PeriodGroup;
  apiPreset?: string;
}

export const PLAYGROUND_PERIODS: Period[] = [
  { id: "mtd", label: "MTD", months: 1, tier: "primary", apiPreset: "MTD" },
  { id: "qtd", label: "QTD", months: 3, tier: "primary", apiPreset: "QTD" },
  { id: "ytd", label: "YTD", months: -1, tier: "primary", apiPreset: "YTD" },
  { id: "1y", label: "1Y", months: 12, tier: "primary", apiPreset: "1Y" },
  { id: "3y", label: "3Y", months: 36, tier: "primary", apiPreset: "3Y" },
  { id: "5y", label: "5Y", months: 60, tier: "primary", apiPreset: "5Y" },
  { id: "3m", label: "3M", months: 3, tier: "overflow", group: "trailing", apiPreset: "3M" },
  { id: "6m", label: "6M", months: 6, tier: "overflow", group: "trailing", apiPreset: "6M" },
  { id: "10y", label: "10Y", months: 120, tier: "overflow", group: "trailing", apiPreset: "10Y" },
  { id: "max", label: "SI (Common)", months: -2, tier: "overflow", group: "inception", apiPreset: "MAX_COMMON" },
  { id: "all", label: "SI (All)", months: -2, tier: "overflow", group: "inception", apiPreset: "MAX" },
  { id: "custom", label: "Custom", months: -3, tier: "custom" },
];

const PERIOD_BY_ID = new Map(PLAYGROUND_PERIODS.map((period) => [period.id, period]));

export function getPeriodTier(period: Period): PeriodTier {
  return period.tier ?? (period.id === "custom" ? "custom" : "primary");
}

export function getPrimaryPeriods(periods: Period[]): Period[] {
  return periods.filter((period) => getPeriodTier(period) === "primary");
}

export function getOverflowPeriods(periods: Period[]): Period[] {
  return periods.filter((period) => getPeriodTier(period) === "overflow");
}

export function getCustomPeriod(periods: Period[]): Period | undefined {
  return periods.find((period) => getPeriodTier(period) === "custom");
}

export function findPeriodById(periodId: string, periods: Period[] = PLAYGROUND_PERIODS): Period | undefined {
  return periods.find((period) => period.id === periodId);
}

export function isOverflowPeriodId(periodId: string, periods: Period[] = PLAYGROUND_PERIODS): boolean {
  const period = findPeriodById(periodId, periods);
  return period ? getPeriodTier(period) === "overflow" : false;
}

export type ApiTimeRange = {
  preset?: string;
  start_date?: string;
  end_date?: string;
};

/** Map a playground period id to the widget API time_range payload. */
export function mapPeriodToApiTimeRange(
  period: string | undefined,
  customDateRange?: [string, string],
): ApiTimeRange {
  if (
    period === "custom" &&
    customDateRange?.[0] &&
    customDateRange?.[1]
  ) {
    return {
      start_date: new Date(customDateRange[0]).toISOString().split("T")[0],
      end_date: new Date(customDateRange[1]).toISOString().split("T")[0],
    };
  }

  const definition = period ? PERIOD_BY_ID.get(period) : undefined;
  if (definition?.apiPreset) {
    return { preset: definition.apiPreset };
  }

  return { preset: "3Y" };
}

/** Approximate client-side date range for statistics table column filtering. */
export function estimatePeriodDateRange(
  period: string | undefined,
  customDateRange?: [string, string],
): { start_date: string; end_date: string } {
  const endDate = new Date();
  let startDate = new Date();

  if (
    period === "custom" &&
    customDateRange?.[0] &&
    customDateRange?.[1]
  ) {
    return {
      start_date: customDateRange[0],
      end_date: customDateRange[1],
    };
  }

  switch (period) {
    case "mtd":
      startDate = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
      break;
    case "qtd": {
      const quarterStartMonth = Math.floor(endDate.getMonth() / 3) * 3;
      startDate = new Date(endDate.getFullYear(), quarterStartMonth, 1);
      break;
    }
    case "ytd":
      startDate = new Date(endDate.getFullYear(), 0, 1);
      break;
    case "3m":
      startDate.setMonth(endDate.getMonth() - 3);
      break;
    case "6m":
      startDate.setMonth(endDate.getMonth() - 6);
      break;
    case "1y":
      startDate.setFullYear(endDate.getFullYear() - 1);
      break;
    case "3y":
      startDate.setFullYear(endDate.getFullYear() - 3);
      break;
    case "5y":
      startDate.setFullYear(endDate.getFullYear() - 5);
      break;
    case "10y":
      startDate.setFullYear(endDate.getFullYear() - 10);
      break;
    case "max":
    case "all":
      startDate = new Date(endDate);
      break;
    default:
      startDate.setFullYear(endDate.getFullYear() - 3);
  }

  return {
    start_date: startDate.toISOString().split("T")[0],
    end_date: endDate.toISOString().split("T")[0],
  };
}
